// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Typography } from '@mui/material';
import type { TFunction } from 'i18next';
import { AdvisoryBankProductType } from '@shared/data-access/types/brokerage-types';
import { AppIcon, AssetLogo } from '@shared/ui-kit';
import type { SvgIconName } from '@shared/ui-kit/components/app-icon';
import { classNames } from '@shared/ui-kit/utils/classnames';
import { maskIban } from '../../utils/bank-utils';
import { getProductTypeLabel } from '../../utils/get-product-type-label';
import type { IlliquidLinkableProduct } from '../../utils/illiquid-linked-products';
import styles from './illiquid-asset-linked-accounts-section.module.css';

const PRODUCT_TYPE_ICON: Record<AdvisoryBankProductType, SvgIconName> = {
  [AdvisoryBankProductType.Account]: 'Building',
  [AdvisoryBankProductType.Card]: 'CreditCard',
  [AdvisoryBankProductType.Loan]: 'Landmark',
  [AdvisoryBankProductType.Portfolio]: 'TrendingUp',
};

export const IlliquidLinkedProductContent = ({
  product,
  t,
  className,
  surface = 'section',
}: {
  readonly product: IlliquidLinkableProduct;
  readonly t: TFunction;
  readonly className?: string;
  /**
   * Row surface: section (linked list) uses page logo box; page (popover) uses
   * section logo box.
   */
  readonly surface?: 'section' | 'page';
}) => (
  <div className={classNames(styles['option-row'], className)}>
    <div
      className={classNames(
        styles['logo-box'],
        surface === 'page' ? styles['logo-box-option'] : undefined,
      )}
    >
      {product.logo_url ? (
        <AssetLogo logoUrl={product.logo_url} className={styles['logo-img']} />
      ) : (
        <AppIcon icon="Building" color="disabled" size="base" />
      )}
    </div>

    <div className={styles['row-text']}>
      <Typography className={styles['row-title']} component="p">
        {product.name}
      </Typography>

      <Typography className={styles['row-subtitle']} component="p">
        <AppIcon
          icon={PRODUCT_TYPE_ICON[product.product_type]}
          color="disabled"
          size="sm"
        />
        {product.institution_name}
        {' · '}
        {getProductTypeLabel(product.product_type, product.type, t)}
        {' · '}
        <span className={classNames(styles['row-iban'], 'money-display')}>
          {maskIban(product.iban, 'last-only')}
        </span>
      </Typography>
    </div>
  </div>
);
